import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/authStore'
import type { ProductStock, ProductStockTxn, StockRefType } from '@/types/app'

/** Returns a map of menuItemId → ProductStock for the whole restaurant */
export function useProductStock() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: queryKeys.productStock(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_stock')
        .select('*')
        .eq('restaurant_id', restaurantId)
      if (error) throw error
      const map = new Map<string, ProductStock>()
      for (const row of (data ?? []) as ProductStock[]) {
        map.set(row.menu_item_id, row)
      }
      return map
    },
    enabled: !!restaurantId,
  })
}

/** Full transaction ledger for one product */
export function useProductStockTxns(menuItemId: string) {
  return useQuery({
    queryKey: queryKeys.productStockTxns(menuItemId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_stock_txns')
        .select('*')
        .eq('menu_item_id', menuItemId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as ProductStockTxn[]
    },
    enabled: !!menuItemId,
  })
}

interface AddStockArgs {
  menuItemId: string
  txnType: 'in' | 'out' | 'adjustment'
  qty: number
  referenceType?: StockRefType
  referenceId?: string
  notes?: string
}

/** Add a single stock transaction (production run, wastage, manual adjustment) */
export function useAddProductStock() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ menuItemId, txnType, qty, referenceType, referenceId, notes }: AddStockArgs) => {
      const { data, error } = await supabase
        .from('product_stock_txns')
        .insert({
          restaurant_id:  restaurantId,
          menu_item_id:   menuItemId,
          txn_type:       txnType,
          qty,
          reference_type: referenceType ?? 'production',
          reference_id:   referenceId ?? null,
          notes:          notes ?? null,
        })
        .select()
        .single()
      if (error) throw error
      return data as ProductStockTxn
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.productStock(restaurantId) })
    },
  })
}

interface SyncInvoiceStockArgs {
  invoiceId: string
  lineItems: { menu_item_id: string | null; qty: number; description: string }[]
  /** 'deduct' when invoice goes to sent, 'restore' when cancelled */
  action: 'deduct' | 'restore'
}

/**
 * Idempotent: deletes any existing invoice stock txns for this invoice,
 * then inserts fresh ones based on current line items.
 * Call 'deduct' when invoice is sent, 'restore' when cancelled.
 */
export function useSyncInvoiceStock() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ invoiceId, lineItems, action }: SyncInvoiceStockArgs) => {
      // Always wipe existing invoice stock txns first (idempotent)
      await supabase
        .from('product_stock_txns')
        .delete()
        .eq('reference_type', 'invoice')
        .eq('reference_id', invoiceId)

      if (action === 'restore') return  // cancel: just delete the out-txns

      const txns = lineItems
        .filter(li => li.menu_item_id && li.qty > 0)
        .map(li => ({
          restaurant_id:  restaurantId,
          menu_item_id:   li.menu_item_id as string,
          txn_type:       'out' as const,
          qty:            li.qty,
          reference_type: 'invoice' as const,
          reference_id:   invoiceId,
          notes:          li.description,
        }))

      if (txns.length > 0) {
        const { error } = await supabase.from('product_stock_txns').insert(txns)
        if (error) throw error
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.productStock(restaurantId) })
    },
  })
}
